import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";

import { cn } from "../lib/utils";

function downloadCsv(
  filename,
  rows,
  columns,
) {
  const headers = columns.map((c) => c.header);
  const escape = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;

  const lines = [
    headers.join(","),
    ...rows.map((r) =>
      columns
        .map((c) => escape(String(r[c.accessorKey] ?? "")))
        .join(","),
    ),
  ].join("\n");

  const blob = new Blob([lines], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function GenericCatalogTable({
  data,
  columns,
  csvColumns,
  className,
  initialPageSize = 25,
}) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [columnsOpen, setColumnsOpen] = useState(false);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      columnVisibility,
      pagination,
    },
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const visibleRows = table.getRowModel().rows;

  const leafColumns = useMemo(() => {
    return table.getAllLeafColumns().filter((c) => c.id !== "expander");
  }, [table]);

  return (
    <div className={cn("space-y-4", className)}>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 items-center gap-3">
          <input
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            placeholder="Search…"
            className="focus-ring h-11 w-full rounded-xl border border-border-subtle bg-white/5 px-3 text-sm text-text-primary placeholder:text-text-tertiary"
          />
          <button
            type="button"
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
            onClick={() => {
              setGlobalFilter("");
              setSorting([]);
              setColumnFilters([]);
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
          >
            Clear
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
            onClick={() =>
              downloadCsv(
                "catalog.csv",
                visibleRows.map((r) => r.original),
                csvColumns,
              )
            }
          >
            Export CSV
          </button>

          <div className="relative">
            <button
              type="button"
              className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-4 text-sm font-semibold text-text-primary hover:bg-white/10"
              onClick={() => setColumnsOpen((v) => !v)}
            >
              Columns ▾
            </button>
            {columnsOpen && (
              <div className="absolute right-0 top-12 z-20 w-64 rounded-2xl border border-border-subtle bg-background-tertiary/90 p-3 shadow-glass backdrop-blur-2xl">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Toggle columns
                </div>
                <div className="mt-2 space-y-2">
                  {leafColumns.map((col) => (
                    <label
                      key={col.id}
                      className="flex cursor-pointer items-center justify-between rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary hover:bg-white/10"
                    >
                      <span>{String(col.columnDef.header ?? col.id)}</span>
                      <input
                        type="checkbox"
                        checked={col.getIsVisible()}
                        onChange={col.getToggleVisibilityHandler()}
                      />
                    </label>
                  ))}
                </div>
                <button
                  type="button"
                  className="mt-3 w-full rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm font-semibold text-text-primary hover:bg-white/10"
                  onClick={() => setColumnsOpen(false)}
                >
                  Close
                </button>
              </div>
            )}
          </div>

          <select
            value={pagination.pageSize}
            onChange={(e) =>
              setPagination({ pageIndex: 0, pageSize: Number(e.target.value) })
            }
            className="focus-ring h-11 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary"
          >
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
            <option value={100}>100 / page</option>
          </select>
        </div>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full border-separate border-spacing-0">
            <thead className="sticky top-0 z-10 bg-background-tertiary/90 backdrop-blur-xl">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="relative border-b border-border-subtle px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted"
                      style={{ width: header.getSize() }}
                    >
                      <button
                        type="button"
                        className="inline-flex items-center gap-2"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        {header.column.getIsSorted() === "asc" && "▲"}
                        {header.column.getIsSorted() === "desc" && "▼"}
                      </button>
                      {header.column.getCanResize() && (
                        <div
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className={cn(
                            "absolute right-0 top-0 h-full w-1 cursor-col-resize select-none",
                            header.column.getIsResizing() && "bg-white/20",
                          )}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <tr className="border-b border-border-subtle hover:bg-white/5">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="border-b border-border-subtle px-3 py-3 align-top text-sm text-text-secondary"
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {row.getIsExpanded() && (
                    <tr>
                      <td
                        colSpan={row.getVisibleCells().length}
                        className="border-b border-border-subtle bg-white/3 px-4 py-4"
                      >
                        <pre className="whitespace-pre-wrap break-words text-xs text-text-secondary">
                          {JSON.stringify(row.original, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-border-subtle px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-text-tertiary">
            Showing {table.getRowModel().rows.length} items (filtered)
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="focus-ring h-10 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary hover:bg-white/10 disabled:opacity-40"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              Prev
            </button>
            <button
              type="button"
              className="focus-ring h-10 rounded-xl border border-border-subtle bg-white/5 px-3 text-sm font-semibold text-text-primary hover:bg-white/10 disabled:opacity-40"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
